import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TrendingUp, TrendingDown, Minus, Target, Info, AlertCircle } from "lucide-react";

export const pct = (n) => `${Math.round((Number(n) || 0) * 100)}%`;
export const currency = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export const statusColors = {
  green: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  yellow: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  red: "bg-red-500/20 text-red-300 border-red-500/30",
  none: "bg-slate-500/20 text-slate-400 border-slate-500/30",
};

// MA-BE-082: Metric confidence status colors
const confidenceColors = {
  exact: "text-emerald-400",
  estimated: "text-amber-400",
  partial_data: "text-orange-400",
  missing_data: "text-slate-500",
  not_applicable: "text-slate-500",
};

const confidenceLabels = {
  exact: "Exact",
  estimated: "Estimated",
  partial_data: "Partial",
  missing_data: "No Data",
  not_applicable: "N/A",
};

// DASH-EMAIL-010: Availability vs. Confidence — two independent axes
// (EMAIL-ANALYTICS-SPEC-001 §18). Confidence (status/reason, above)
// answers "how strong/complete is the underlying evidence." Availability
// (below) answers "can this metric truthfully be produced at this
// grain/population right now." A KPICard MAY carry both, or either alone.
const AVAILABILITY_CONFIG = {
  PARTIAL: { label: "Partial", badgeClass: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  PENDING: { label: "Pending", badgeClass: "bg-slate-500/15 text-slate-400 border-slate-500/30" },
  UNAVAILABLE: { label: "Unavailable", badgeClass: "bg-slate-500/15 text-slate-400 border-slate-500/30" },
  STALE: { label: "Stale", badgeClass: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  ERROR: { label: "Error", badgeClass: "bg-red-500/15 text-red-300 border-red-500/30" },
};

// Availability states that must never let a stale/absent metric render as a
// number (EMAIL-ANALYTICS-SPEC-001 §18.1: UNAVAILABLE MUST NEVER be
// presented as numeric 0 merely to satisfy a UI shape; PENDING/ERROR carry
// the same "no trustworthy current value" requirement, per §18.2).
// AVAILABLE, PARTIAL, and STALE all retain a real, trustworthy value.
const NON_NUMERIC_AVAILABILITY = new Set(["UNAVAILABLE", "PENDING", "ERROR"]);

export function DirectionIcon({ direction, isLowerBetter = false }) {
  if (direction === "up") {
    return <TrendingUp className={`h-4 w-4 ${isLowerBetter ? "text-red-400" : "text-emerald-400"}`} />;
  }
  if (direction === "down") {
    return <TrendingDown className={`h-4 w-4 ${isLowerBetter ? "text-emerald-400" : "text-red-400"}`} />;
  }
  return <Minus className="h-4 w-4 text-slate-400" />;
}

/**
 * MA-BE-082: Confidence indicator for metrics
 * DASH-EMAIL-P1-IMPL-008: exported so EmailFunnelCard.jsx can render the
 * exact same indicator per funnel stage, rather than a second
 * implementation of the same status -> icon/tooltip convention.
 */
export function ConfidenceIndicator({ status, reason }) {
  if (!status || status === "exact") return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <AlertCircle
            className={`h-3 w-3 ${confidenceColors[status] || confidenceColors.missing_data} cursor-help`}
            data-testid="metric-confidence-indicator"
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-slate-900 border-slate-700 text-xs max-w-xs">
          <p className="font-medium">{confidenceLabels[status] || status}</p>
          {reason && <p className="text-slate-400 mt-1">{reason}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * DASH-EMAIL-010: Availability indicator — a separate axis from
 * ConfidenceIndicator above (see AVAILABILITY_CONFIG's comment). Renders
 * nothing for AVAILABLE (or when `availability` is omitted), matching
 * ConfidenceIndicator's own "exact/absent shows nothing" convention.
 */
function AvailabilityBadge({ availability, availabilityReason, asOf }) {
  const config = AVAILABILITY_CONFIG[availability];
  if (!config) return null;

  const label = availability === "STALE" && asOf ? `${config.label} · as of ${asOf}` : config.label;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`rounded border px-1 py-0.5 text-[9px] font-medium uppercase tracking-wide cursor-help ${config.badgeClass}`}
            data-testid={`metric-availability-${availability.toLowerCase()}`}
          >
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-slate-900 border-slate-700 text-xs max-w-xs">
          <p className="font-medium">{config.label}</p>
          {availabilityReason && <p className="text-slate-400 mt-1">{availabilityReason}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function KPICard({
  title,
  value,
  format = "number",
  change,
  direction,
  benchmark,
  icon: Icon,
  isLowerBetter = false,
  suffix = "",
  // MA-BE-082: confidence props
  status,
  reason,
  // DASH-EMAIL-010: availability props (additive; all optional)
  availability,
  availabilityReason,
  asOf,
  // EMAIL-DASHBOARD-KPI-002/003: optional node attached to the END of the
  // title's own text flow (rendered INSIDE the title <p>, not as a flex
  // sibling) -- see the render below for why this specific placement
  // matters. Additive/backward-compatible: omitting it renders this card
  // byte-for-byte as before this checkpoint.
  titleAddon = null,
}) {
  let displayValue = value;

  if (availability && NON_NUMERIC_AVAILABILITY.has(availability)) {
    // EMAIL-ANALYTICS-SPEC-001 §18.1: never render an absent/untrustworthy
    // metric as "0" or any other numeric-looking value — regardless of
    // whatever `value` happens to be passed alongside it.
    displayValue = "—";
  } else if (value === null || value === undefined) {
    displayValue = "—";
  } else if (format === "percent") {
    displayValue = pct(value);
  } else if (format === "currency") {
    displayValue = currency(value);
  } else if (format === "hours") {
    displayValue = `${Number(value || 0).toFixed(1)}`;
  } else if (format === "number") {
    displayValue = Number(value || 0).toLocaleString();
  }

  const formattedTarget = benchmark?.target != null
    ? format === "percent" ? pct(benchmark.target)
    : format === "currency" ? currency(benchmark.target)
    : format === "hours" ? `${benchmark.target}h`
    : benchmark.target
    : null;

  const varianceDisplay = benchmark?.variance_percent != null
    ? `${benchmark.variance_percent >= 0 ? "+" : ""}${(benchmark.variance_percent * 100).toFixed(1)}%`
    : null;

  const benchmarkStatus = benchmark?.status || "none";
  const lowConfidence = benchmark?.low_confidence || false;

  return (
    <Card className="glass border-white/10" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              {/* EMAIL-DASHBOARD-KPI-003: titleAddon is rendered INSIDE this
                  <p>, as part of the title's own inline text flow, rather
                  than as a sibling flex item alongside it. A flex-sibling
                  icon shares ONE unwrapped row with the (separately
                  text-wrapping) title <p>, so on a long/wrapping title it
                  visually drifts toward wherever that row's geometry
                  happens to place it -- typically near the card's own
                  decorative icon on the right -- rather than following the
                  title's own last word. Nesting the addon inside the <p> as
                  ordinary inline content makes it wrap WITH the text, by
                  the browser's normal inline layout, with no JS
                  measurement and no absolute positioning: it always ends
                  up immediately after the final word, on whichever line
                  that word lands on. */}
              <p className="text-xs font-medium text-slate-300/70 uppercase tracking-wide">
                {title}
                {titleAddon && (
                  <span className="inline-flex align-middle ml-1">{titleAddon}</span>
                )}
              </p>
              {/* MA-BE-082: Show confidence indicator for estimated/missing metrics */}
              <ConfidenceIndicator status={status} reason={reason} />
              {/* DASH-EMAIL-010: Show availability indicator (independent axis) */}
              <AvailabilityBadge availability={availability} availabilityReason={availabilityReason} asOf={asOf} />
            </div>
            <p className="text-2xl font-bold text-slate-100">
              {displayValue}
              {suffix && <span className="text-sm ml-1 text-slate-400">{suffix}</span>}
            </p>
          </div>
          {Icon && (
            <div className="rounded-lg bg-indigo-500/20 p-2">
              <Icon className="h-5 w-5 text-indigo-300" />
            </div>
          )}
        </div>
        
        <div className="mt-3 flex items-center gap-2">
          {change !== null && change !== undefined && (
            <>
              <DirectionIcon direction={direction} isLowerBetter={isLowerBetter} />
              <span className={`text-sm font-medium ${
                direction === "up" 
                  ? (isLowerBetter ? "text-red-400" : "text-emerald-400")
                  : direction === "down"
                  ? (isLowerBetter ? "text-emerald-400" : "text-red-400")
                  : "text-slate-400"
              }`}>
                {Math.abs(change).toFixed(1)}%
              </span>
              <span className="text-xs text-slate-400">vs prev</span>
            </>
          )}
        </div>

        {benchmark?.target != null && (
          <div className="mt-2 pt-2 border-t border-white/5">
            <div className="flex items-center justify-between gap-2">
              <Badge className={`text-xs border ${statusColors[benchmarkStatus]}`} data-testid={`kpi-status-${benchmarkStatus}`}>
                {benchmarkStatus === "green" && "✓ On Target"}
                {benchmarkStatus === "yellow" && "⚠ Warning"}
                {benchmarkStatus === "red" && "✗ Critical"}
                {benchmarkStatus === "none" && "- No Target"}
              </Badge>

              <div className="flex items-center gap-2 text-xs">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-slate-400 flex items-center gap-1">
                        <Target className="h-3 w-3" />
                        {formattedTarget}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-slate-900 border-slate-700 text-xs">
                      Target from {benchmark.scope_used || "global"} scope
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {varianceDisplay && (
                  <span className={`flex items-center gap-1 ${
                    benchmarkStatus === "green" ? "text-emerald-400" :
                    benchmarkStatus === "yellow" ? "text-amber-400" :
                    benchmarkStatus === "red" ? "text-red-400" :
                    "text-slate-400"
                  }`}>
                    {benchmark.variance_percent >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {varianceDisplay}
                  </span>
                )}

                {lowConfidence && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-amber-400" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="bg-slate-900 border-slate-700 text-xs">
                        Low sample size - results may not be representative
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
